import { useState } from "react";
import { Menu, X, Shield, Smartphone, Building2 } from "lucide-react";
import SiteReveal from "../shared/SiteReveal";
import { BRAND as BASE_BRAND, NAV, PRODUCTS, DIGITAL, RATES, BRANCHES, FAQ } from "./firstBankData";
import { getDemoProfile } from "../shared/industryCatalog";
import "../shared/siteReveal.css";
import "./FirstBankSite.css";

export default function FirstBankSite({ embed = false, profileId = "first-digital" }) {
  const profile = getDemoProfile("bank", profileId);
  const BRAND = profile?.brand ?? BASE_BRAND;
  const theme = profile?.theme ?? {};
  const [menuOpen, setMenuOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(0);
  const [branchQ, setBranchQ] = useState("");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", product: "", message: "" });

  const scroll = (id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const filteredBranches = BRANCHES.filter(
    (b) => !branchQ || b.name.includes(branchQ) || b.addr.includes(branchQ),
  );

  const submit = (e) => {
    e.preventDefault();
    setModal(true);
    setForm({ name: "", phone: "", product: "", message: "" });
  };

  return (
    <div
      className={`fb-site${embed ? " fb-site--embed" : ""}`}
      style={{
        "--fb-navy": theme.navy ?? "#0f2b46",
        "--fb-blue": theme.blue ?? "#2c5f8a",
        "--fb-gold": theme.gold ?? "#b8963e",
      }}
    >
      <div className="fb-topbar">
        <div className="fb-wrap fb-topbar__inner">
          <span>{BRAND.hours}</span>
          <span>고객센터 {BRAND.phone}</span>
        </div>
      </div>

      <header className="fb-header">
        <div className="fb-header__inner">
          <a href="#fb-top" className="fb-brand" onClick={(e) => { e.preventDefault(); scroll("fb-top"); }}>
            <strong>{BRAND.name}</strong>
            <span>{BRAND.nameEn}</span>
          </a>
          <nav className="fb-nav">
            {NAV.map((n) => (
              <a key={n.id} href={`#${n.id}`} onClick={(e) => { e.preventDefault(); scroll(n.id); }}>{n.label}</a>
            ))}
          </nav>
          <button type="button" className="fb-btn fb-btn--navy" onClick={() => scroll("inquiry")}>상담 신청</button>
          <button type="button" className="fb-menu" onClick={() => setMenuOpen((v) => !v)}>{menuOpen ? <X size={20} /> : <Menu size={20} />}</button>
        </div>
        {menuOpen && <nav className="fb-mobile">{NAV.map((n) => <a key={n.id} href={`#${n.id}`} onClick={(e) => { e.preventDefault(); scroll(n.id); }}>{n.label}</a>)}</nav>}
      </header>

      <section id="fb-top" className="fb-hero">
        <div className="fb-wrap fb-hero__grid">
          <div>
            <span className="fb-eyebrow">Digital Banking</span>
            <h1>{BRAND.slogan}</h1>
            <p>{BRAND.intro}</p>
            <div className="fb-hero__btns">
              <button type="button" className="fb-btn fb-btn--navy" onClick={() => scroll("products")}>상품 안내</button>
              <button type="button" className="fb-btn fb-btn--outline" onClick={() => scroll("digital")}>앱 서비스</button>
            </div>
          </div>
          <div className="fb-hero__cards">
            <div className="fb-hero__card"><Shield size={22} /><span>금융보안 인증</span></div>
            <div className="fb-hero__card"><Smartphone size={22} /><span>모바일 뱅킹</span></div>
            <div className="fb-hero__card"><Building2 size={22} /><span>전국 영업점</span></div>
          </div>
        </div>
      </section>

      <section id="products" className="fb-section">
        <div className="fb-wrap">
          <SiteReveal><div className="fb-head"><h2>금융 상품</h2></div></SiteReveal>
          <div className="fb-grid4">
            {PRODUCTS.map((p) => (
              <SiteReveal key={p.title}>
                <article className="fb-card">
                  <h3>{p.title}</h3>
                  <p>{p.desc}</p>
                  <ul>{p.items.map((i) => <li key={i}>{i}</li>)}</ul>
                  <button type="button" className="fb-link" onClick={() => scroll("inquiry")}>상담 신청</button>
                </article>
              </SiteReveal>
            ))}
          </div>
        </div>
      </section>

      <section id="digital" className="fb-section fb-section--gray">
        <div className="fb-wrap">
          <SiteReveal><div className="fb-head"><h2>디지털 뱅킹</h2></div></SiteReveal>
          <div className="fb-digital">
            {DIGITAL.map((d) => (
              <SiteReveal key={d.title}>
                <article><h4>{d.title}</h4><p>{d.desc}</p></article>
              </SiteReveal>
            ))}
          </div>
        </div>
      </section>

      <section id="rates" className="fb-section">
        <div className="fb-wrap">
          <SiteReveal><div className="fb-head"><h2>금리 안내</h2><p>2026년 3월 기준 · 세전</p></div></SiteReveal>
          <SiteReveal>
            <table className="fb-rates">
              <thead><tr><th>상품</th><th>금리</th><th>비고</th></tr></thead>
              <tbody>
                {RATES.map((r) => (
                  <tr key={r.name}><td>{r.name}</td><td><strong>{r.rate}</strong></td><td>{r.note}</td></tr>
                ))}
              </tbody>
            </table>
          </SiteReveal>
        </div>
      </section>

      <section id="branch" className="fb-section fb-section--gray">
        <div className="fb-wrap">
          <SiteReveal><div className="fb-head"><h2>영업점 찾기</h2></div></SiteReveal>
          <SiteReveal>
            <div className="fb-branch-search">
              <input type="text" placeholder="지역 또는 지점명 검색" value={branchQ} onChange={(e) => setBranchQ(e.target.value)} />
            </div>
            <div className="fb-branches">
              {filteredBranches.map((b) => (
                <article key={b.name}>
                  <h4>{b.name}</h4>
                  <p>{b.addr}</p>
                  <span>{b.tel}</span>
                </article>
              ))}
            </div>
          </SiteReveal>
        </div>
      </section>

      <section className="fb-section">
        <div className="fb-wrap">
          <SiteReveal>
            <div className="fb-faq">
              <h3>자주 묻는 질문</h3>
              {FAQ.map((item, i) => (
                <div key={item.q} className={`fb-faq__item${faqOpen === i ? " is-open" : ""}`}>
                  <button type="button" onClick={() => setFaqOpen(faqOpen === i ? -1 : i)}>{item.q}</button>
                  {faqOpen === i && <p>{item.a}</p>}
                </div>
              ))}
            </div>
          </SiteReveal>
        </div>
      </section>

      <section id="inquiry" className="fb-section fb-section--gray">
        <div className="fb-wrap fb-inquiry">
          <SiteReveal>
            <div>
              <h2>금융 상담 신청</h2>
              <p>관심 상품을 남겨 주시면 전문 상담사가 연락드립니다.</p>
              <p className="fb-inquiry__tel">고객센터 {BRAND.phone}</p>
            </div>
          </SiteReveal>
          <SiteReveal>
            <form className="fb-form" onSubmit={submit}>
              <div className="fb-form-row">
                <div><label>이름</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><label>연락처</label><input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              </div>
              <div>
                <label>관심 상품</label>
                <select required value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })}>
                  <option value="">선택</option>
                  <option>예금·적금</option>
                  <option>대출</option>
                  <option>카드</option>
                  <option>자산관리</option>
                </select>
              </div>
              <div><label>문의 내용</label><textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></div>
              <button type="submit" className="fb-btn fb-btn--navy">상담 신청</button>
            </form>
          </SiteReveal>
        </div>
      </section>

      <footer className="fb-footer">
        <div className="fb-wrap">
          <strong>{BRAND.name}</strong>
          <p>금융소비자보호법에 따른 안내 · 예금자보호법 적용</p>
          <span>© 2026 {BRAND.nameEn} · Portfolio by DSiteSolution</span>
        </div>
      </footer>

      {modal && (
        <div className="fb-modal-overlay" onClick={() => setModal(false)}>
          <div className="fb-modal" onClick={(e) => e.stopPropagation()}>
            <h3>상담 신청이 접수되었습니다</h3>
            <p>담당 상담사가 확인 후 연락드리겠습니다.</p>
            <button type="button" className="fb-btn fb-btn--navy" onClick={() => setModal(false)}>확인</button>
          </div>
        </div>
      )}
    </div>
  );
}
